import { useEffect } from 'react';

import { useGateValue, useStatsigClient } from '@coursera-web/statsig';

import { IdTypes, createFeatureScope } from 'bundles/product-features';
import {
  ORG_SCOPED_HOME_OVERRIDE_ORG_IDS,
  ORG_SCOPED_HOME_STATSIG_GATE,
} from 'bundles/unified-common/constants/orgScopedHomeStatsig';

const { useProductFeatures } = /* @__PURE__ */ createFeatureScope({
  enterprise: {
    enableOrgScopedHome: false,
  },
});

/**
 * Checks if a specific organization is opted into org-scoped home.
 *
 * Unlike useIsOrgScopedHome (which reads from page-level context),
 * this hook checks a given org ID directly. Used in the program switcher
 * to determine per-org link behavior.
 *
 * Requires BOTH:
 * 1. The Statsig gate `org-scoped-home` passes (stable-ID rollout, acts as kill switch)
 * 2. The given org is in ORG_SCOPED_HOME_OVERRIDE_ORG_IDS OR has the `enableOrgScopedHome`
 *    product feature enabled
 */
export function useIsOrgOptedIntoOrgScopedHome(orgId: string): boolean {
  // @ts-expect-error FIXME: please avoid using the `disableExposureLog` and use qualifying events to filter exposures
  const userInOrgScopedRollout = useGateValue(ORG_SCOPED_HOME_STATSIG_GATE, { disableExposureLog: true });
  const { checkGate } = useStatsigClient();
  const { features } = useProductFeatures({
    idType: IdTypes.Organization,
    id: orgId,
    skip: !orgId,
  });
  const featureEnabled = orgId ? features.get('enterprise', 'enableOrgScopedHome') : false;

  const isEnabled =
    userInOrgScopedRollout && !!orgId && (ORG_SCOPED_HOME_OVERRIDE_ORG_IDS.has(orgId) || featureEnabled);

  useEffect(() => {
    if (isEnabled) {
      checkGate(ORG_SCOPED_HOME_STATSIG_GATE);
    }
  }, [isEnabled, checkGate]);

  return isEnabled;
}
